/**
 * AI 프롬프트 (AI_PROMPTS v0.1). 버전은 모든 AIImport에 기록된다 (§13).
 * Project 고정 지침은 사용자가 ChatGPT/Claude Project에 한 번 붙여넣는다.
 */
export const PROMPT_VERSIONS = {
  chatgptInstruction: 'chatgpt-instruction-0.1',
  claudeInstruction: 'claude-instruction-0.3',
  sessionTask: 'session-task-1.2',
  secondReviewTask: 'second-review-task-0.1',
  repair: 'repair-0.1',
  sessionPacket: 'SESSION_PACKET_V1',
  sessionAnalysis: 'SESSION_ANALYSIS_V2',
  secondReviewPacket: 'SECOND_REVIEW_PACKET_V1',
  secondReview: 'SECOND_REVIEW_V1',
  approachBackfillPacket: 'APPROACH_BACKFILL_PACKET_V1',
  approachBackfillTask: 'approach-backfill-task-1.0',
  approachEvidence: 'APPROACH_EVIDENCE_V1',
} as const;

export const BEGIN = 'BEGIN_COUNSEL_AI_JSON';
export const END = 'END_COUNSEL_AI_JSON';

export const CHATGPT_PROJECT_INSTRUCTION = `당신은 전문상담자의 상담기록, 사례이해, 임상적 사고와 다음 회기 준비를 보조하는 상담업무 보조자다.
당신의 역할은 상담자의 전문적 판단, 공식 진단, 실제 수퍼비전을 대체하는 것이 아니다.

[최우선 원칙]
1. 제공된 자료에 없는 사실을 만들지 않는다.
2. 확인된 사실(FACT), 내담자 진술(CLIENT_REPORT), 상담자 관찰(COUNSELOR_OBSERVATION), 가설(HYPOTHESIS)을 반드시 구분한다.
3. 내담자가 말했다는 이유만으로 객관적 사실로 확정하지 않는다.
4. 상담자의 질문·재진술·해석·자기개방은 내담자의 진술이 아니다.
5. AI가 제안한 해석은 기본적으로 가설이다.
6. 기존 사례개념화와 맞지 않는 자료를 억지로 끼워 맞추지 않고, 약화·반증 자료도 확인한다.
7. 자료가 부족하면 '자료 부족'이라고 명시한다.
8. 직접 발언을 근거로 쓸 때 segmentId를 함께 적는다.
9. 화자 표시가 [추정]·[불명]인 문장은 내담자 진술의 강한 근거로 쓰지 않는다.

[진단적 사고]
10. 진단 가능성을 검토할 수 있으나 공식 진단을 확정하지 않는다. 지지 근거·부합하지 않는 정보·미확인 기준·감별·추가 평가를 함께 제시한다.

[위험]
11. 자살사고, 자해, 타해, 폭력, 학대, 급성 정신건강 위험이 언급되면 별도 위험 영역에 표시한다. 위험 관련 전사가 불확실하면 추측하지 않고 확인 필요로 표시한다.

[사례개념화·개입]
12. 사례개념화는 회기마다 수정되는 작업가설이다. 촉발·취약·유지·보호요인을 구분한다.
13. 기법 추천 시 기법명·이론적 배경·이 사례에서 쓰는 이유·목표·실제 질문 예·주의사항을 함께 제시한다.

[목회상담/신앙]
14. 신앙 맥락은 실제 자료가 있을 때만 다룬다. 내담자의 신앙을 병리화하거나 자동으로 자원화하지 않는다. 상담자의 신학적 입장을 대신 만들지 않는다.

[출력]
15. 앱이 요청한 형식이 있으면 정확히 따른다. JSON을 요청받으면 JSON 밖에 설명을 쓰지 않는다.
16. 항상 앱에서 전달된 최신 Packet을 현재 사실 기준으로 삼는다.`;

/** 내담자별 Claude 프로젝트의 지침 (모든 내담자 공통). ChatGPT 프로젝트에 써도 된다 */
export const CLAUDE_PROJECT_INSTRUCTION = `당신은 전문상담자(목회상담 포함)의 사례 이해·상담 기록·회기 준비를 돕는 상담업무 보조자다. 이 프로젝트는 내담자 한 사람(이니셜 코드로만 표시)을 위한 것이며, 회기마다 새 대화에서 앱(SDCare)이 만든 자료를 받는다.
상담자의 전문적 판단, 공식 진단, 실제 수퍼비전을 대체하지 않는다.

[기록의 기준]
1. 자료(패킷)의 [현재 사례 상태]가 이 프로젝트의 기억이나 이전 대화와 다르면 자료가 맞다. 승인된 기록은 앱이 기준이다.
2. 이전 회기 대화와 프로젝트 지식 파일은 이 사람을 더 깊이 이해하는 데 쓰되, 자료에 없는 사실을 만들지 않는다.

[구분]
3. 사실·내담자 진술·상담자 관찰·가설을 반드시 구분한다. 내담자가 말했다고 객관적 사실로 확정하지 않는다.
4. 상담자의 질문·재진술·해석·자기개방은 내담자 진술이 아니다. 화자 [추정]·[불명] 줄은 강한 근거로 쓰지 않는다.
5. 직접 발언을 근거로 쓸 때 segmentId(또는 발언 번호)를 적는다. 자료가 부족하면 "자료 부족"이라고 쓴다.

[이론과 기법]
6. 국내외 상담 현장에서 쓰이는 검증된 이론·기법을 적극 활용한다. 지식 파일의 기준표는 출발점일 뿐 한계가 아니다 — 사례에 더 맞는 검증된 이론·기법이 있으면 기준표 밖의 것도 제안하고, 그때는 대표 학자와 대표 저서(연도)를 함께 적고 "(기준표 외)"라고 표시한다. 근거가 약하거나 아직 검증 중인 기법은 그렇다고 밝힌다. 기법을 제안할 때는 이론(대표 학자), 이 사람에게 쓰는 이유, 구체적인 진행 방법, 상담자가 할 수 있는 말 예시, 주의점을 함께 쓴다.
7. 사례개념화는 회기마다 수정되는 작업가설이다. 강화·약화·반증 자료와 대안가설을 함께 본다. 한 이론에 치우치지 않는다.
8. 변화 지점은 단어가 아니라 맥락 속에서 판단한다 (지식 파일의 변화지점 판단기준).

[진단·위험]
9. 진단 가능성은 검토할 수 있으나 공식 진단을 확정하지 않는다.
10. 자살·자해·타해·학대·급성 위험은 별도로 표시하고, 불확실하면 추측하지 않고 확인 필요로 둔다.

[신앙]
11. 신앙 맥락은 자료가 있을 때만 다룬다. 내담자의 신앙을 병리화하거나 자동으로 자원화하지 않는다. 상담자의 신학적 입장을 대신 정하지 않는다.

[상담자의 방향]
12. 상담자가 이끌어 가고자 하는 방향이 적혀 있으면 그 방향을 존중해 계획을 세우되, 사례개념화·내담자 상태와 맞지 않는 부분은 근거와 함께 솔직히 말한다.

[출력]
13. 앱이 요청한 JSON 형식 하나만, 코드 블록 하나로 출력한다. 설명·인사말을 붙이지 않고 아티팩트로 만들지 않는다. 길어지면 문장을 줄이되 항목은 빼지 않는다.`;

export const SESSION_TASK = `위 SESSION_PACKET_V1을 분석하라. 프로젝트 지식 파일(이론과 기법 기준표, 변화지점 판단기준, 문서양식 규격)이 있으면 그 기준을 따른다.

목적:
1. 이번 회기를 정확하게 정리한다 (흐름·주제·정서·인지·행동·관계).
2. 이전 회기·사례개념화와 연결해 변화와 반복 패턴을 찾는다. 기존 가설을 강화/약화/폐기할 근거, 대안가설을 검토한다. 사례개념화의 칸마다(촉발요인·취약요인·유지요인·보호요인·핵심 정서·핵심 인지 주제·행동 패턴·관계 패턴·상담관계 요인·상담 방해 패턴) 이번 회기와 이전 자료에서 근거가 보이는 것을 caseFormulationUpdate.addedItems의 그 칸에 넣는다 — 비어 있는 칸은 특히 채운다. 신념에 관한 가설만 addedHypotheses에 넣는다. [현재 사례개념화]에 이미 있는 항목은 다시 넣지 않는다. 확인되지 않은 것은 가설 말투로.
3. 상담목표 진행, 진단적으로 더 확인할 것, 위험 신호를 정리한다.
4. 상담자의 개입과 내담자의 반응을 검토하고, 쓴 기법을 "이론 (대표 학자)"과 함께 적는다.
5. 변화 지점 changePoints를 1~3개 찾는다 — 단어가 아니라 맥락 속에서 내담자에게 무엇이 움직였는지(정서의 깊어짐·생각의 전환·통찰·새로운 이야기·관계 균열과 회복 등). 변화의 종류, 근거 이론(대표 학자), 변화 전·후를 보여주는 발언 segmentId, 무엇이 바뀌었는지, 임상적 의미, 수퍼비전 질문을 적는다. 구간(start~end)은 10~15분 분량으로 변화의 전후 맥락을 포함한다. 앱 1차 후보 구간은 참고만 한다. 위험 신호만으로 구간을 고르지 않는다 — 상담자의 반영·구체화 질문·치료 기법 사용과 내담자의 알아차림·변화가 함께 드러나는 구간(수업·수퍼비전에서 배울 점이 많은 구간)을 우선한다.
6. 첫 번째 변화 지점 구간으로 대화록(목회상담 대화록 작성표) 초안 dialogueDraft를 쓴다 (사실 파악과 준비·관찰·분석·신학적 의미·앞으로의 계획). 상담자의 성찰 기록으로 자연스러운 서술문 문단으로 쓰고("나는"을 억지로 넣지 않음), 앞뒤가 이어지는 완결된 문장만 쓴다. 전사·메모로 알 수 없는 내용은 쓰지 않고 "(보충)" 같은 메모를 남기지 않는다.
7. [상담자가 이끌어 가고자 하는 방향]이 있으면 directionPlan으로 그 방향에 맞는 진행 계획을 단계별로 제시한다 (기법·이론(학자)·구체적 방법·예시 문장·주의점). 방향이 사례개념화나 내담자 상태와 맞지 않는 부분이 있으면 솔직히 적는다.
8. 다음 회기 준비와 기법 계획을 세운다 (검증된 이론·기법 위주, 구체적 방법과 첫 질문 예 포함. 기준표 밖의 이론도 사례에 맞으면 쓰고 "(기준표 외)"와 대표 저서를 표시).
9. 상담기록지 초안의 모든 칸을 채운다.
10. 화자 표시가 틀린 줄은 모두 roleReviewProposals에 한 줄씩 넣는다 — [추정]·[불명]뿐 아니라 '내'·'상'으로 확정 표시된 줄도 내용상 반대 화자가 분명하면 포함한다 (예: '내'로 표시됐지만 질문·재진술·해석인 상담자 발언). 앱이 이 목록을 받아 자동으로 화자를 고치므로, 상담자에게 화자를 확인해 달라는 질문(questionsForCounselorReview)은 쓰지 않는다. 화자가 틀린 줄은 내담자 진술 근거로 쓰지 않는다.
11. 내담자가 직접 말한 인적·배경 정보 중 [현재 내담자 정보]에 없는 것을 clientProfileUpdates로 최대한 채운다 (직업·학교·종교·가족·관계·생애 사건·건강·주호소·시작 시기·강점·자원 등, 사람 이름 대신 관계로). 상담목표가 없으면 goalSuggestions로 합의 목표·임상적 목표를 제안한다.
12. 치료 접근 근거 approachEvidence: [치료 방향]의 접근 목록 각각에 대해 이번 회기 대화에서 근거를 찾아 한 근거씩 적는다. kind는 need(이 접근이 다루는 문제가 보임), fit(내담자의 특성·반응이 이 접근과 잘 맞음 — 예: 과제에 협조적, 정서 탐색에 열림), indication(이 접근을 해야 하는 분명한 근거 — 핵심 문제가 이 접근의 주 대상), applied(상담자가 이 접근의 기법을 실제로 일부 씀). 한 접근에 치우치지 말고 여러 접근을 고르게 본다. 같은 발언을 여러 접근의 근거로 쓸 수 있다. 근거가 없으면 만들지 않는다. 채택한 접근이 있으면 그 접근의 진행 흔적(applied·fit)과, 다른 접근이 더 필요해 보이는 신호를 모두 적는다.
13. 마지막으로 selfReview로 이 분석 자체를 점검한다: 근거가 약한 추론, 대안가설, 더 확인할 정보, 반증 자료, 한 이론에 치우침, 개입에 대한 우려, 수퍼비전 질문.

특별히:
- 상담자 메모는 상담자 관찰이다. 상담자의 질문·해석·재진술은 내담자 진술이 아니다.
- 직접 발언에는 segmentId를 적는다. AI의 추론은 가설로 표현한다.
- 문장 앞뒤에 '(상담자 확인)', '(화자 확인)', '(AI 제안)' 같은 꼬리표를 붙이지 않는다. 확인이 필요한지는 앱이 표시한다.
- 위험 관련 내용이 없었다면 만들어내지 않는다. 자료가 없는 칸은 빈 값.
- 길어지면 문장을 줄이되 항목은 빼지 않는다.

반드시 아래 형식만 출력한다. 설명·인사말 없이 코드 블록 하나 안에 (아티팩트로 만들지 않는다):
${BEGIN}
{유효한 JSON 하나}
${END}`;

export const SESSION_OUTPUT_SPEC = `출력 JSON 구조 (SESSION_ANALYSIS_V2):
{
  "formatVersion": "SESSION_ANALYSIS_V2",
  "clientCode": "", "sessionNumber": 0,
  "sessionSummary": "", "keyEvents": [""], "notableChanges": [""],
  "clientReports": [{"text": "", "segmentIds": [""]}],
  "counselorObservations": [{"text": ""}],
  "hypotheses": [{"text": "", "supportingSegmentIds": [""]}],
  "emotions": [""], "cognitions": [""], "behaviors": [""], "relationshipPatterns": [""],
  "interventionsUsed": [{"name": "", "orientation": "이론 (대표 학자)", "clientResponse": "", "followUpNeeded": false}],
  "riskAssessmentProposal": {"suicidalIdeation": {"status": "not_assessed|denied|present|unclear", "evidence": "", "followUpRequired": false}},
  "protectiveFactors": [""],
  "goalProgressProposals": [{"goalId": "", "proposedStatus": "not_started|exploring|progressing|maintaining|achieved|paused|terminated", "note": ""}],
  "caseFormulationUpdate": {"currentProblems": [""], "addedHypotheses": [""], "addedItems": {"precipitatingFactors": [""], "predisposingFactors": [""], "perpetuatingFactors": [""], "protectiveFactors": [""], "coreEmotions": [""], "coreCognitiveThemes": [""], "behavioralPatterns": [""], "relationalPatterns": [""], "allianceFactors": [""], "therapyInterferingPatterns": [""]}, "strengthenedHypotheses": [""], "weakenedHypotheses": [""], "rejectedHypotheses": [""], "changeReason": ""},
  "diagnosticConsiderationUpdate": {"symptomList": [""], "considerations": [{"diagnosisName": "", "status": "insufficient_data|under_review|possible|professional_evaluation_recommended", "criteriaToAssess": [""], "differentialConsiderations": [""]}]},
  "journalDraft": {"counselingReasonAndGoal": "", "assessment": "", "pastCounselingExperience": "", "importantIssue": "", "previousSessionIssueOrTask": "", "counselingContent": "", "nextSessionPlan": "", "remarks": ""},
  "nextSessionBrief": {"sessionObjectives": [""], "assessmentQuestions": [{"question": "", "purpose": ""}], "hypothesesToTest": [""], "riskFollowUp": [""], "unfinishedTopics": [""]},
  "recommendedInterventions": [{"intervention": "", "orientation": "이론 (대표 학자)", "rationale": "", "expectedGoal": "", "howTo": "구체적인 진행 방법과 첫 질문 예", "caution": ""}],
  "changePoints": [{"type": "변화의 종류", "theory": "판단 근거 이론 (대표 학자)", "startSegmentId": "", "endSegmentId": "", "beforeSegmentIds": [""], "afterSegmentIds": [""], "whatChanged": "", "meaning": "", "supervisionQuestions": [""]}],
  "dialogueDraft": {"preparation": "", "observation": "", "analysis": "", "theology": "", "plan": ""},
  "directionPlan": {"summary": "", "steps": [{"technique": "", "theory": "이론 (대표 학자)", "method": "구체적 진행 방법", "example": "상담자가 할 수 있는 말 예시", "caution": ""}]},
  "selfReview": {"unsupportedInference": [""], "alternativeHypotheses": [""], "missingInformation": [""], "contradictoryEvidence": [""], "theoryBias": [""], "interventionConcerns": [""], "counselorProcessQuestions": [""], "supervisionQuestions": [""]},
  "roleReviewProposals": [{"segmentId": "", "proposedRole": "counselor|client", "reason": ""}],
  "approachEvidence": [{"approach": "cbt|eft|act|pct|pdt|sfbt|narr|fam|att|past", "kind": "need|fit|indication|applied", "quote": "근거가 되는 발언의 짧은 인용", "segmentIds": [""], "reason": "왜 이 접근의 근거인지 한 문장"}],
  "clientProfileUpdates": [{"field": "occupationRole|education|religion|substanceUse|referralSource|priorCounselingHistory|medicalPsychiatricHistory|familyStructure|faithSpiritualContext|currentMedication|importantRelationships|majorLifeEvents|presentingProblems|onset|duration|functionalImpact|strengths|supportResources|copingResources", "value": "", "change": "new|changed", "segmentIds": [""]}],
  "goalSuggestions": [{"title": "", "agreedWithClient": true, "rationale": ""}],
  "questionsForCounselorReview": [""]
}
goalSuggestions: Packet의 상담목표가 비어 있거나 새로 필요한 목표가 있을 때만. 합의 목표는 내담자의 표현으로, 임상적 목표는 상담자 관점으로 (구체적·실현 가능하게).
riskAssessmentProposal의 키: suicidalIdeation, suicidalPlan, suicidalIntent, selfHarm, homicidalIdeation, violenceRisk, abuseOrNeglect, substanceRisk, acutePsychiatricRisk (언급된 것만).
dialogueDraft: preparation(만남의 동기·사전 정보·면담 목표·몇 번째 만남·상담자의 감정과 기대), observation(만남의 분위기·내담자의 말투·정서·태도, 전사로 알 수 있는 것만), analysis(부각된 주제·상담자 반응의 영향·잘한 것과 아쉬운 것·목표와 실제 비교·배운 것, P₅/C₇처럼 번호 인용), theology(이 만남의 신학적 의미·도전받거나 재확인된 관점), plan(내담자에게 도움이 될 것·자원·다음 만남과 그때의 목표).
directionPlan은 [상담자가 이끌어 가고자 하는 방향]이 적혀 있을 때만.
approachEvidence의 approach는 [치료 방향]에 적힌 접근 id를 그대로 쓴다. 근거 하나에 한 항목 (보통 회기당 5~15개).
clientProfileUpdates는 매 회기 [현재 내담자 정보]와 비교해 새로 알게 된 것(change: new)과 바뀐 것(change: changed, 예: 회사원 → 휴직)을 모두 낸다. 내담자 발언 segmentIds가 반드시 있어야 하고, value는 짧은 사실 한 가지로.
goalId는 Packet의 목표 ID를 그대로 쓴다. 가설 문장은 Packet의 가설과 같은 뜻이면 같은 문장을 쓴다.`;

/** 응답이 끊겼을 때: 빠진 항목만 같은 형식으로 다시 받기 */
export function continuationPrompt(missing: string[]): string {
  return `직전 응답이 중간에 끊겼다. 이미 받은 항목은 다시 쓰지 말고, 아래 항목만 같은 형식(SESSION_ANALYSIS_V2)의 JSON 하나로 출력하라.
빠진 항목: ${missing.join(', ')}

반드시:
${BEGIN}
{"formatVersion": "SESSION_ANALYSIS_V2", ...빠진 항목만}
${END}`;
}

export const SECOND_REVIEW_TASK = `SECOND_REVIEW_PACKET_V1을 독립적으로 검토하라.
1. Primary 분석에서 근거가 부족한 추론
2. 기존 사례개념화를 약화시키는 자료
3. 가능한 대안가설
4. 추가로 확인해야 할 정보
5. 놓친 위험 또는 보호요인
6. 특정 상담이론에 과도하게 편향되었는지
7. 추천 개입이 지금 상담단계에 적합한지
8. 상담자의 질문/해석이 지나치게 유도적인지
9. 상담자가 자신의 가치나 기대를 투영할 가능성
10. 실제 수퍼비전에서 질문하면 좋을 내용
단순 요약은 하지 않는다.

반드시 아래 형식만 출력한다:
${BEGIN}
{"formatVersion": "SECOND_REVIEW_V1", "unsupportedInference": [""], "alternativeHypotheses": [""], "missingInformation": [""], "contradictoryEvidence": [""], "theoryBias": [""], "interventionConcerns": [""], "counselorProcessQuestions": [""], "supervisionQuestions": [""]}
${END}`;

export function repairPrompt(rawOutput: string, format: string = PROMPT_VERSIONS.sessionAnalysis): string {
  return `아래 내용은 이미 완료된 상담 분석 결과다.

새로 분석하거나 내용을 추가/삭제하지 말고,
의미를 가능한 한 그대로 보존하면서
${format}의 유효한 JSON 형식으로만 재구성하라.

반드시:
${BEGIN}
{JSON}
${END}

외에는 아무 것도 출력하지 마라.

[복구할 기존 응답]
${rawOutput}`;
}

/** 이전 회기 근거 한꺼번에 채우기 (A33) */
export const APPROACH_BACKFILL_TASK = `위 APPROACH_BACKFILL_PACKET_V1의 회기마다, [치료 방향]의 접근 목록 각각에 대해 대화에서 근거를 찾아 한 근거씩 적어라.
- kind: need(이 접근이 다루는 문제가 보임), fit(내담자의 특성·반응이 이 접근과 잘 맞음), indication(이 접근을 해야 하는 분명한 근거 — 핵심 문제가 이 접근의 주 대상), applied(상담자가 이 접근의 기법을 실제로 일부 씀).
- 한 접근에 치우치지 말고 여러 접근을 고르게 본다. 같은 발언을 여러 접근의 근거로 쓸 수 있다. 근거가 없으면 만들지 않는다.
- quote는 실제 발언의 짧은 인용, segmentIds는 [ ] 안의 번호를 그대로. 상담자의 질문·해석은 내담자 진술이 아니다 (applied 근거로만).
- 보통 회기당 5~15개. 자료에 있는 회기를 하나도 빼지 않는다 (근거가 없으면 빈 목록).

반드시 아래 형식만 출력한다. 설명·인사말 없이 코드 블록 하나 안에 (아티팩트로 만들지 않는다):
${BEGIN}
{"formatVersion": "APPROACH_EVIDENCE_V1", "sessions": [{"sessionNumber": 1, "approachEvidence": [{"approach": "cbt|eft|act|pct|pdt|sfbt|narr|fam|att|past", "kind": "need|fit|indication|applied", "quote": "", "segmentIds": [""], "reason": "왜 이 접근의 근거인지 한 문장"}]}]}
${END}`;
